import { createActionGuard } from "@/utils/createActionGuard";

const GUARD_CLICK_STATE = Symbol("guardClickState");

const normalizeBindingValue = (value) => {
  if (typeof value === "function") {
    return {
      handler: value,
      mode: "lock",
      wait: undefined,
    };
  }

  if (value && typeof value === "object") {
    return {
      handler: value.handler,
      mode: value.mode || "lock",
      wait: value.wait,
    };
  }

  return {
    handler: null,
    mode: "lock",
    wait: undefined,
  };
};

const normalizeModifiers = (modifiers = {}) => ({
  block: Boolean(modifiers.block),
  capture: Boolean(modifiers.capture),
  prevent: Boolean(modifiers.prevent),
  stop: Boolean(modifiers.stop),
});

const getBindingInstance = (binding) =>
  binding.instance?.proxy || binding.instance || null;

const hasSameGuardOptions = (current, next) =>
  current.mode === next.mode && current.wait === next.wait;

const isElementDisabled = (el) =>
  Boolean(
    el.disabled ||
      el.getAttribute("disabled") !== null ||
      el.getAttribute("aria-disabled") === "true" ||
      el.classList?.contains("is-disabled"),
  );

const createGuard = (state) =>
  createActionGuard(
    function (...args) {
      // 记录本次事件是否真正通过 guard，供 block 模式决定是否继续向子元素传播。
      state.acceptedEvent = args[0];
      const handler = state.config.handler;

      if (typeof handler !== "function") {
        return undefined;
      }

      return handler.apply(state.instance || this, args);
    },
    {
      mode: state.config.mode,
      wait: state.config.wait,
    },
  );

const bindGuardClick = (el, binding) => {
  const state = {
    config: normalizeBindingValue(binding.value),
    modifiers: normalizeModifiers(binding.modifiers),
    instance: getBindingInstance(binding),
    guard: null,
    listener: null,
    listenerOptions: null,
    acceptedEvent: null,
  };

  state.guard = createGuard(state);
  state.listener = function (event) {
    if (isElementDisabled(el)) {
      return;
    }

    // 指令替代 @click 后仍需保留原事件修饰语义，避免表格行、上传遮罩等父级点击被误触发。
    if (state.modifiers.prevent) {
      event.preventDefault();
    }

    if (state.modifiers.stop) {
      event.stopPropagation();
    }

    state.acceptedEvent = null;
    state.guard.call(this, event);

    // 分页等组件仍通过自身 change 事件传递参数；重复点击只需在到达子元素前被截断。
    if (state.modifiers.block && state.acceptedEvent !== event) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  state.listenerOptions = { capture: state.modifiers.capture };
  el.addEventListener("click", state.listener, state.listenerOptions);
  el[GUARD_CLICK_STATE] = state;
};

const updateGuardClick = (el, binding) => {
  const state = el[GUARD_CLICK_STATE];

  if (!state) {
    bindGuardClick(el, binding);
    return;
  }

  const nextConfig = normalizeBindingValue(binding.value);

  state.instance = getBindingInstance(binding);
  state.modifiers = normalizeModifiers(binding.modifiers);

  if (hasSameGuardOptions(state.config, nextConfig)) {
    state.config = nextConfig;
    return;
  }

  state.guard.cancel?.();
  state.config = nextConfig;
  state.guard = createGuard(state);
};

const unbindGuardClick = (el) => {
  const state = el[GUARD_CLICK_STATE];

  if (!state) {
    return;
  }

  el.removeEventListener("click", state.listener, state.listenerOptions);
  state.guard.cancel?.();
  delete el[GUARD_CLICK_STATE];
};

export const guardClickDirective = {
  mounted: bindGuardClick,
  updated: updateGuardClick,
  unmounted: unbindGuardClick,
};

export default {
  install(app) {
    app.directive("guard-click", guardClickDirective);
  },
};
